import {
  Avatar,
  Badge,
  Box,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  Stack,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { IoSearch } from 'react-icons/io5';
import { RiCheckLine, RiQuestionAnswerLine, RiInformationLine } from 'react-icons/ri';
import { MdOutlineKeyboardArrowDown, MdOutlineSmartToy, MdCompareArrows } from 'react-icons/md';
import { BsQuestionCircle } from 'react-icons/bs';
import {
  ComparisonType,
  useGetPerspectiveByDtTopicProjectLazyQuery,
  useGetAnswerSimilaritiesLazyQuery,
} from '../../generated/graphql';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { formatDateShort } from '../../utils/formatDate';
import { ModelCardData } from './ProjectView';

// Similarity → green/yellow/red/gray, per agreed thresholds.
export const getSimilarityStyle = (similarity: number | null | undefined) => {
  if (similarity == null) {
    return { scheme: 'gray', label: 'No comparison', border: HelivanColor.border.primary };
  }
  const pct = `${Math.round(similarity * 100)}% similar`;
  if (similarity >= 0.9) return { scheme: 'green', label: pct, border: '#22c55e' };
  if (similarity >= 0.7) return { scheme: 'yellow', label: pct, border: '#eab308' };
  return { scheme: 'red', label: pct, border: '#ef4444' };
};

// Plan-readiness score (1–7) → Chakra color scheme / border color. >=5 is "safe to
// execute as written" (green), 4 is the borderline lowest-passing (yellow), <4 is not
// ready (red).
export const getQualityScheme = (score: number | null | undefined) => {
  if (score == null) return 'gray';
  if (score >= 5) return 'green';
  if (score >= 4) return 'yellow';
  return 'red';
};
export const getQualityColor = (score: number | null | undefined) => {
  if (score == null) return HelivanColor.border.primary;
  if (score >= 5) return '#22c55e';
  if (score >= 4) return '#eab308';
  return '#ef4444';
};

// Strip the meta wrapper a coding agent adds when its file-writing tool is disabled
// (e.g. "I've drafted the plan.md but the write needs your permission… Here's what it
// covers:" … "Want me to retry the save?"), leaving just the plan content.
export const cleanPlanText = (text?: string | null): string => {
  if (!text) return '';
  let t = text.trim();
  const m = t.match(/here'?s what (it covers|the plan (?:covers|includes)):?/i);
  if (m && m.index !== undefined) {
    const after = t.slice(t.indexOf('\n', m.index + m[0].length) + 1).trim();
    if (after.length > 40) t = after;
  }
  // Leading "Here's the plan:" style preamble
  t = t.replace(/^(here'?s|here is)\b[^\n]*\bplan\b[^\n]*:?\s*\n+/i, '');
  // Trailing offer to save/retry the file
  t = t.replace(/\n+[^\n]*\b(want me to (?:retry|save)|shall i save|i can save it|let me know if[^\n]*save)[^\n]*$/i, '').trim();
  return t || text.trim();
};

interface ProjectTabViewFaqProps {
  allDigitalTwins: {
    id: string;
    name: string;
  }[];
  selectedTwinIds: Set<string>;
  handleSelectedTwinIdsChange: (newSelection: Set<string>) => void;
  setDigitalTwinId: (id: string | undefined) => void;
  digitalTwinId?: string;
  modelCardData?: ModelCardData;
  allTopics: string[];
  setModelCardData: (data: ModelCardData | undefined) => void;
  projectId: string;
  topic: string;
  // Other versions of this project, for the optional "compare with" view.
  timelineProjects?: { id: string; dateCreated: string }[];
}

export const ProjectTabViewFaq = ({
  allDigitalTwins,
  selectedTwinIds,
  setDigitalTwinId,
  handleSelectedTwinIdsChange,
  digitalTwinId,
  modelCardData,
  allTopics,
  setModelCardData,
  projectId,
  topic,
  timelineProjects = [],
}: ProjectTabViewFaqProps) => {
  const [selectedTopic, setSelectedTopic] = useState('topic');
  const [getPerspectiveByDt] = useGetPerspectiveByDtTopicProjectLazyQuery({
    fetchPolicy: 'network-only',
  });

  // Optional comparison: version A is the current project; the user can pick an
  // earlier version (B) to see how answers changed, with similarity shading.
  const [compareVersionId, setCompareVersionId] = useState<string | null>(null);
  const otherVersions = useMemo(
    () => timelineProjects.filter((tp) => tp.id !== projectId),
    [timelineProjects, projectId],
  );
  const compareVersion = timelineProjects.find((tp) => tp.id === compareVersionId);

  const [getPerspectiveB, { data: dataB }] = useGetPerspectiveByDtTopicProjectLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [getSimilarities, { data: simData }] = useGetAnswerSimilaritiesLazyQuery({
    fetchPolicy: 'network-only',
  });

  // Fetch version B's answers + per-question similarity when a comparison is active.
  useEffect(() => {
    if (digitalTwinId && selectedTopic && compareVersionId) {
      const topicArg = selectedTopic === 'Overall' ? 'topic' : selectedTopic;
      getPerspectiveB({
        variables: { dtId: digitalTwinId, topic: topicArg, projectId: compareVersionId },
      });
      getSimilarities({
        variables: {
          agentId: digitalTwinId,
          topic: topicArg,
          versionAId: projectId,
          versionBId: compareVersionId,
        },
      });
    }
  }, [digitalTwinId, selectedTopic, compareVersionId, projectId, getPerspectiveB, getSimilarities]);

  const answersB = useMemo(() => {
    const ops = dataB?.getPerspectiveByDtTopicProject?.opinions ?? [];
    return new Map(ops.map((o) => [o.question, o.answer]));
  }, [dataB]);

  const similarityByQuestion = useMemo(() => {
    const map = new Map<string, number | null>();
    (simData?.getAnswerSimilarities ?? []).forEach((s) => map.set(s.question, s.similarity ?? null));
    return map;
  }, [simData]);

  const isComparing = !!compareVersionId;

  // Current version's answers (A) come from modelCardData; merge with B by question.
  const comparedQuestions = useMemo(() => {
    const result: { question: string; answerA?: string; answerB?: string }[] = [];
    const seen = new Set<string>();
    (modelCardData?.questions ?? []).forEach((q) => {
      seen.add(q.question);
      result.push({ question: q.question, answerA: q.answer, answerB: answersB.get(q.question) });
    });
    answersB.forEach((ans, q) => {
      if (!seen.has(q)) result.push({ question: q, answerA: undefined, answerB: ans });
    });
    return result;
  }, [modelCardData, answersB]);

  // Persona search state
  const [personaSearchQuery, setPersonaSearchQuery] = useState('');
  const [showPersonaDropdown, setShowPersonaDropdown] = useState(false);

  const filteredPersonas = useMemo(() => {
    if (!personaSearchQuery.trim()) return allDigitalTwins.slice(0, 10);
    const query = personaSearchQuery.toLowerCase();
    return allDigitalTwins.filter((twin) =>
      twin.name.toLowerCase().includes(query)
    );
  }, [allDigitalTwins, personaSearchQuery]);

  useEffect(() => {
    if (digitalTwinId && selectedTopic) {
      getPerspectiveByDt({
        variables: {
          dtId: digitalTwinId,
          topic: selectedTopic === 'Overall' ? 'topic' : selectedTopic,
          projectId,
        },
        onCompleted: (data) => {
          const perspective = data.getPerspectiveByDtTopicProject;
          const entityName = perspective.digitalTwin?.name ?? perspective.customAgent?.name ?? 'Unknown';
          const entityId = perspective.digitalTwin?.id ?? perspective.customAgent?.id ?? 'unknown';
          setModelCardData({
            digitalTwin: perspective.digitalTwin ?? undefined,
            customAgent: perspective.customAgent ?? undefined,
            entityId,
            entityName,
            similar: (perspective.comparisons ?? []).filter((c) => c.type === ComparisonType.Similar),
            dissimilar: (perspective.comparisons ?? []).filter((c) => c.type === ComparisonType.Dissimilar),
            questions: (perspective.opinions ?? []).map(
              (opinion) => {
                return {
                  question: opinion.question,
                  answer: opinion.answer,
                };
              }
            ),
            topic: `${entityName} on ${perspective.topic === 'topic' ? topic : perspective.topic}`,
            projectId,
            qualityScore: perspective.qualityScore ?? null,
            judgeNotes: perspective.judgeNotes ?? null,
          });
        },
      });
    }
  }, [selectedTopic, digitalTwinId]);

  const selectedPersona = allDigitalTwins.find((t) => t.id === digitalTwinId);

  return (
    <Flex
      width="full"
      height="full"
      flexDirection="column"
      maxWidth="900px"
      mx="auto"
    >
      {/* Header with controls */}
      <Flex
        width="full"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
        pb={4}
        borderBottom="1px solid"
        borderBottomColor={HelivanColor.border.primary}
      >
        <HStack spacing={3}>
          <Flex
            width="36px"
            height="36px"
            borderRadius="8px"
            bg={HelivanColor.hover.lightBlue}
            alignItems="center"
            justifyContent="center"
          >
            <RiQuestionAnswerLine size={18} color={HelivanColor.text.helivanBlue} />
          </Flex>
          <VStack align="start" spacing={0}>
            <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueDark}>
              Deep Dive
            </HelivanText>
            <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
              Explore detailed Q&A responses
            </HelivanText>
          </VStack>
        </HStack>

        <HStack spacing={3}>
          {/* Agent selector dropdown */}
          <Menu isLazy>
            <MenuButton
              as={Box}
              px={3}
              py={2}
              borderRadius="6px"
              bg="white"
              border="1px solid"
              borderColor={!digitalTwinId ? '#FFB4B4' : HelivanColor.border.primary}
              cursor="pointer"
              minWidth="180px"
              _hover={{ borderColor: HelivanColor.text.helivanBlue }}
            >
              <HStack justify="space-between">
                <HStack spacing={2}>
                  <MdOutlineSmartToy size={14} color={HelivanColor.text.blueMid} />
                  <Text
                    fontSize="13px"
                    color={selectedPersona ? HelivanColor.text.blueDark : HelivanColor.text.blueMid}
                    noOfLines={1}
                    maxWidth="120px"
                  >
                    {selectedPersona ? selectedPersona.name : 'Select agent'}
                  </Text>
                </HStack>
                <MdOutlineKeyboardArrowDown size={16} color={HelivanColor.text.blueMid} />
              </HStack>
            </MenuButton>
            <Portal>
              <MenuList
                zIndex={1400}
                maxHeight="300px"
                overflowY="auto"
                boxShadow="0 4px 20px rgba(0,0,0,0.15)"
                borderRadius="8px"
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                py={2}
              >
                {/* Search within dropdown */}
                <Box px={2} pb={2}>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                      <IoSearch size={14} color={HelivanColor.text.blueMid} />
                    </InputLeftElement>
                    <Input
                      placeholder="Search..."
                      value={personaSearchQuery}
                      onChange={(e) => setPersonaSearchQuery(e.target.value)}
                      borderRadius="6px"
                      bg={HelivanColor.background.secondary}
                      border="none"
                      fontSize="13px"
                      _placeholder={{ color: HelivanColor.text.blueMid }}
                    />
                  </InputGroup>
                </Box>
                {filteredPersonas.map((persona) => (
                  <MenuItem
                    key={persona.id}
                    onClick={() => {
                      setDigitalTwinId(persona.id);
                      handleSelectedTwinIdsChange(new Set([persona.id]));
                      setPersonaSearchQuery('');
                    }}
                    fontSize="13px"
                    py={2}
                    px={3}
                    bg={digitalTwinId === persona.id ? HelivanColor.hover.lightBlue : 'white'}
                    _hover={{ bg: HelivanColor.background.secondary }}
                  >
                    <HStack spacing={2} width="full" justify="space-between">
                      <HStack spacing={2}>
                        <MdOutlineSmartToy size={14} color={HelivanColor.text.blueMid} />
                        <Text color={HelivanColor.text.blueDark}>{persona.name}</Text>
                      </HStack>
                      {digitalTwinId === persona.id && (
                        <RiCheckLine size={14} color={HelivanColor.text.helivanBlue} />
                      )}
                    </HStack>
                  </MenuItem>
                ))}
                {filteredPersonas.length === 0 && (
                  <Box px={3} py={2}>
                    <Text fontSize="13px" color={HelivanColor.text.gray}>No results found</Text>
                  </Box>
                )}
              </MenuList>
            </Portal>
          </Menu>

          {/* Topic selector dropdown */}
          <Menu isLazy>
            <MenuButton
              as={Box}
              px={3}
              py={2}
              borderRadius="6px"
              bg="white"
              border="1px solid"
              borderColor={HelivanColor.border.primary}
              cursor="pointer"
              minWidth="140px"
              _hover={{ borderColor: HelivanColor.text.helivanBlue }}
            >
              <HStack justify="space-between">
                <Text
                  fontSize="13px"
                  color={HelivanColor.text.blueDark}
                  noOfLines={1}
                  maxWidth="100px"
                >
                  {selectedTopic === 'topic' ? 'Overall' : selectedTopic}
                </Text>
                <MdOutlineKeyboardArrowDown size={16} color={HelivanColor.text.blueMid} />
              </HStack>
            </MenuButton>
            <Portal>
              <MenuList
                zIndex={1400}
                maxHeight="300px"
                overflowY="auto"
                boxShadow="0 4px 20px rgba(0,0,0,0.15)"
                borderRadius="8px"
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                py={2}
              >
                {allTopics.map((tpc, idx) => (
                  <MenuItem
                    key={idx}
                    onClick={() => setSelectedTopic(tpc)}
                    fontSize="13px"
                    py={2}
                    px={3}
                    bg={selectedTopic === tpc ? HelivanColor.hover.lightBlue : 'white'}
                    _hover={{ bg: HelivanColor.background.secondary }}
                  >
                    <HStack spacing={2} width="full" justify="space-between">
                      <Text color={HelivanColor.text.blueDark}>
                        {tpc === 'topic' ? 'Overall' : tpc}
                      </Text>
                      {selectedTopic === tpc && (
                        <RiCheckLine size={14} color={HelivanColor.text.helivanBlue} />
                      )}
                    </HStack>
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          </Menu>

          {/* Optional: compare the current version against an earlier one */}
          {otherVersions.length > 0 && (
            <Menu isLazy>
              <MenuButton
                as={Box}
                px={3}
                py={2}
                borderRadius="6px"
                bg={isComparing ? HelivanColor.hover.lightBlue : 'white'}
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                cursor="pointer"
                minWidth="160px"
                _hover={{ borderColor: HelivanColor.text.helivanBlue }}
              >
                <HStack justify="space-between">
                  <HStack spacing={2}>
                    <MdCompareArrows size={14} color={HelivanColor.text.blueMid} />
                    <Text fontSize="13px" color={HelivanColor.text.blueDark} noOfLines={1} maxWidth="120px">
                      {compareVersion ? `vs ${formatDateShort(compareVersion.dateCreated)}` : 'Compare with…'}
                    </Text>
                  </HStack>
                  <MdOutlineKeyboardArrowDown size={16} color={HelivanColor.text.blueMid} />
                </HStack>
              </MenuButton>
              <Portal>
                <MenuList
                  zIndex={1400}
                  maxHeight="300px"
                  overflowY="auto"
                  boxShadow="0 4px 20px rgba(0,0,0,0.15)"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor={HelivanColor.border.primary}
                  py={2}
                >
                  <MenuItem
                    onClick={() => setCompareVersionId(null)}
                    fontSize="13px"
                    py={2}
                    px={3}
                    bg={!compareVersionId ? HelivanColor.hover.lightBlue : 'white'}
                    _hover={{ bg: HelivanColor.background.secondary }}
                  >
                    <HStack spacing={2} width="full" justify="space-between">
                      <Text color={HelivanColor.text.blueDark}>None (single version)</Text>
                      {!compareVersionId && <RiCheckLine size={14} color={HelivanColor.text.helivanBlue} />}
                    </HStack>
                  </MenuItem>
                  {otherVersions.map((tp) => (
                    <MenuItem
                      key={tp.id}
                      onClick={() => setCompareVersionId(tp.id)}
                      fontSize="13px"
                      py={2}
                      px={3}
                      bg={compareVersionId === tp.id ? HelivanColor.hover.lightBlue : 'white'}
                      _hover={{ bg: HelivanColor.background.secondary }}
                    >
                      <HStack spacing={2} width="full" justify="space-between">
                        <Text color={HelivanColor.text.blueDark}>{formatDateShort(tp.dateCreated)}</Text>
                        {compareVersionId === tp.id && <RiCheckLine size={14} color={HelivanColor.text.helivanBlue} />}
                      </HStack>
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
          )}
        </HStack>
      </Flex>

      {/* FAQ content */}
      <Flex
        flex={1}
        overflowY="auto"
        flexDirection="column"
      >
        {modelCardData ? (
          isComparing ? (
          <VStack spacing={4} width="full" align="stretch">
            {/* Column headers: current vs compared version */}
            <Grid templateColumns="1fr 1fr" gap={4}>
              <GridItem>
                <HStack spacing={3} p={3} bg={HelivanColor.hover.lightBlue} borderRadius="8px">
                  <Avatar size="sm" bg="white" name={modelCardData.entityName} color={HelivanColor.text.helivanBlue} />
                  <VStack align="start" spacing={0}>
                    <Text fontSize="13px" fontWeight="600" color={HelivanColor.text.blueDark}>Current</Text>
                    <Text fontSize="11px" color={HelivanColor.text.blueMid}>{modelCardData.questions.length} responses</Text>
                  </VStack>
                </HStack>
              </GridItem>
              <GridItem>
                <HStack spacing={3} p={3} bg={HelivanColor.hover.lightBlue} borderRadius="8px">
                  <Avatar size="sm" bg="white" name={modelCardData.entityName} color={HelivanColor.text.helivanBlue} />
                  <VStack align="start" spacing={0}>
                    <Text fontSize="13px" fontWeight="600" color={HelivanColor.text.blueDark}>
                      {compareVersion ? formatDateShort(compareVersion.dateCreated) : 'Earlier version'}
                    </Text>
                    <Text fontSize="11px" color={HelivanColor.text.blueMid}>{answersB.size} responses</Text>
                  </VStack>
                </HStack>
              </GridItem>
            </Grid>

            {/* Similarity color key */}
            <HStack spacing={3} flexWrap="wrap" px={1}>
              <HStack spacing={1}>
                <Text fontSize="11px" fontWeight={600} color={HelivanColor.text.blueMid}>
                  How much each answer moved
                </Text>
                <Tooltip
                  label="Each answer is compared to the earlier version. Green = ≥90% similar (barely changed), yellow = 70–90%, red = under 70% (a big shift), gray = one version didn't answer."
                  placement="top"
                  hasArrow
                >
                  <span>
                    <Icon as={RiInformationLine} boxSize="13px" color={HelivanColor.text.blueMid} cursor="help" />
                  </span>
                </Tooltip>
              </HStack>
              {[
                { c: '#22c55e', t: 'Barely changed' },
                { c: '#eab308', t: 'Some' },
                { c: '#ef4444', t: 'A lot' },
                { c: HelivanColor.border.primary, t: 'Unanswered' },
              ].map((k) => (
                <HStack key={k.t} spacing={1}>
                  <Box width="10px" height="10px" borderRadius="2px" bg={k.c} />
                  <Text fontSize="11px" color={HelivanColor.text.blueMid}>{k.t}</Text>
                </HStack>
              ))}
            </HStack>

            {comparedQuestions.map((item, idx) => {
              const sim = getSimilarityStyle(similarityByQuestion.get(item.question));
              return (
                <Box
                  key={idx}
                  bg="white"
                  border="1px solid"
                  borderColor={HelivanColor.border.primary}
                  borderLeft="4px solid"
                  borderLeftColor={sim.border}
                  borderRadius="12px"
                  overflow="hidden"
                >
                  <Flex
                    p={4}
                    bg={HelivanColor.background.secondary}
                    borderBottom="1px solid"
                    borderBottomColor={HelivanColor.border.primary}
                    alignItems="center"
                    gap={3}
                    justifyContent="space-between"
                  >
                    <HStack alignItems="flex-start" gap={3}>
                      <Flex width="28px" height="28px" borderRadius="6px" bg={HelivanColor.hover.lightBlue} alignItems="center" justifyContent="center" flexShrink={0}>
                        <BsQuestionCircle size={14} color={HelivanColor.text.helivanBlue} />
                      </Flex>
                      <Text fontSize="14px" fontWeight="500" color={HelivanColor.text.blueDark} lineHeight="1.5">
                        {item.question}
                      </Text>
                    </HStack>
                    <Tooltip
                      label={item.answerA && item.answerB ? 'Semantic similarity of the two answers' : 'One version has no answer for this question'}
                      hasArrow
                      placement="top"
                    >
                      <Badge colorScheme={sim.scheme} flexShrink={0} fontSize="11px">{sim.label}</Badge>
                    </Tooltip>
                  </Flex>
                  <Grid templateColumns="1fr 1fr" gap={0}>
                    <GridItem p={4} borderRight="1px solid" borderRightColor={HelivanColor.border.primary}>
                      <Text fontSize="14px" color={item.answerA ? HelivanColor.text.blueDark : HelivanColor.text.gray} lineHeight="1.6" whiteSpace="pre-wrap">
                        {item.answerA ? cleanPlanText(item.answerA) : 'No response in this version'}
                      </Text>
                    </GridItem>
                    <GridItem p={4}>
                      <Text fontSize="14px" color={item.answerB ? HelivanColor.text.blueDark : HelivanColor.text.gray} lineHeight="1.6" whiteSpace="pre-wrap">
                        {item.answerB ? cleanPlanText(item.answerB) : 'No response in this version'}
                      </Text>
                    </GridItem>
                  </Grid>
                </Box>
              );
            })}
          </VStack>
          ) : (
          <VStack spacing={4} width="full" align="stretch">
            {/* Agent info header */}
            <HStack
              spacing={3}
              p={4}
              bg="white"
              border="1px solid"
              borderColor={HelivanColor.border.primary}
              borderRadius="12px"
            >
              <Avatar
                size="md"
                bg={HelivanColor.hover.lightBlue}
                name={modelCardData.entityName}
                color={HelivanColor.text.helivanBlue}
              />
              <VStack align="start" spacing={0}>
                <Text fontSize="15px" fontWeight="600" color={HelivanColor.text.blueDark}>
                  {modelCardData.entityName}
                </Text>
                <Text fontSize="12px" color={HelivanColor.text.blueMid}>
                  {modelCardData.questions.length} questions answered
                </Text>
              </VStack>
            </HStack>

            {/* Planning-probe: plan-quality score + judge narrative (when present) */}
            {(modelCardData.qualityScore != null || modelCardData.judgeNotes) && (
              <Box
                bg="white"
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                borderLeft="4px solid"
                borderLeftColor={getQualityColor(modelCardData.qualityScore)}
                borderRadius="12px"
                p={4}
              >
                <HStack justify="space-between" mb={modelCardData.judgeNotes ? 2 : 0}>
                  <HStack spacing={1}>
                    <Text fontSize="13px" fontWeight={600} color={HelivanColor.text.blueDark}>
                      Plan readiness
                    </Text>
                    <Tooltip
                      label="An LLM judge rates each plan 1–7 for how ready it is to hand to a coding agent (framing, design, decomposition, risk, verification, integrity). Tracked over time to surface regressions."
                      placement="top"
                      hasArrow
                    >
                      <span>
                        <Icon as={RiInformationLine} boxSize="13px" color={HelivanColor.text.blueMid} cursor="help" />
                      </span>
                    </Tooltip>
                  </HStack>
                  {modelCardData.qualityScore != null && (
                    <Badge colorScheme={getQualityScheme(modelCardData.qualityScore)} fontSize="12px">
                      {Math.round(modelCardData.qualityScore)} / 7
                    </Badge>
                  )}
                </HStack>
                {modelCardData.judgeNotes && (
                  <Text fontSize="13px" color={HelivanColor.text.blueMid} lineHeight="1.6">
                    {modelCardData.judgeNotes}
                  </Text>
                )}
              </Box>
            )}

            {/* Q&A cards */}
            {modelCardData.questions.map((question, idx) => (
              <Box
                key={idx}
                bg="white"
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                borderRadius="12px"
                overflow="hidden"
              >
                {/* Question */}
                <Flex
                  p={4}
                  bg={HelivanColor.background.secondary}
                  borderBottom="1px solid"
                  borderBottomColor={HelivanColor.border.primary}
                  alignItems="flex-start"
                  gap={3}
                >
                  <Flex
                    width="28px"
                    height="28px"
                    borderRadius="6px"
                    bg={HelivanColor.hover.lightBlue}
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <BsQuestionCircle size={14} color={HelivanColor.text.helivanBlue} />
                  </Flex>
                  <Text
                    fontSize="14px"
                    fontWeight="500"
                    color={HelivanColor.text.blueDark}
                    lineHeight="1.5"
                  >
                    {modelCardData.qualityScore != null
                      ? (selectedTopic === 'topic' ? 'Plan' : `Plan — ${selectedTopic}`)
                      : question.question}
                  </Text>
                </Flex>

                {/* Answer */}
                <Flex p={4} alignItems="flex-start" gap={3}>
                  <Avatar
                    size="sm"
                    bg={HelivanColor.hover.lightBlue}
                    name={modelCardData.entityName}
                    color={HelivanColor.text.helivanBlue}
                    flexShrink={0}
                  />
                  <Text
                    fontSize="14px"
                    color={HelivanColor.text.blueDark}
                    lineHeight="1.6"
                    whiteSpace="pre-wrap"
                  >
                    {modelCardData.qualityScore != null ? cleanPlanText(question.answer) : question.answer}
                  </Text>
                </Flex>
              </Box>
            ))}
          </VStack>
          )
        ) : (
          <Flex
            width="full"
            height="full"
            alignItems="center"
            justifyContent="center"
            bg="white"
            border="1px solid"
            borderColor={HelivanColor.border.primary}
            borderRadius="12px"
          >
            <Stack
              spacing={4}
              justifyContent="center"
              alignItems="center"
              width="full"
              py={10}
            >
              <Flex
                width="72px"
                height="72px"
                borderRadius="16px"
                bg={HelivanColor.background.secondary}
                alignItems="center"
                justifyContent="center"
              >
                <MdOutlineSmartToy size={36} color={HelivanColor.text.blueMid} />
              </Flex>
              <VStack spacing={1}>
                <Text
                  fontSize="16px"
                  fontWeight="600"
                  color={HelivanColor.text.blueDark}
                  textAlign="center"
                >
                  Select an agent to explore
                </Text>
                <Text
                  fontSize="13px"
                  color={HelivanColor.text.blueMid}
                  textAlign="center"
                  maxWidth="280px"
                >
                  Choose an agent from the dropdown above to view their detailed Q&A responses
                </Text>
              </VStack>
            </Stack>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
